
// src/lib/db.js
import { createRxDatabase, addRxPlugin } from 'rxdb'
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder'
import { RxDBMigrationPlugin } from 'rxdb/plugins/migration-schema'
import { RxDBUpdatePlugin } from 'rxdb/plugins/update'
import { getRxStorageDexie } from 'rxdb/plugins/storage-dexie'
import { SupabaseReplication } from './supabase-replication.js'
import { attachAutoResync, stripSyncMetadata } from './sync.js'
import { supabase } from './supabase'
 
addRxPlugin(RxDBMigrationPlugin)
addRxPlugin(RxDBQueryBuilderPlugin)
addRxPlugin(RxDBUpdatePlugin)
 
// ─── SCHEMA VERSION ────────────────────────────────────────────────────────────
// Bump this whenever a breaking schema change is made.
// On mismatch with the stored version, IndexedDB is wiped and rebuilt clean.
const DB_VERSION = 6
 
const DB_VERSION_KEY = 'elevate_db_version'
 
async function shouldResetDb() {
  try {
    const stored = localStorage.getItem(DB_VERSION_KEY)
    return stored === null || parseInt(stored, 10) < DB_VERSION
  } catch {
    return true
  }
}
 
async function nukeIndexedDb() {
  try {
    const dbs = await indexedDB.databases?.()
    if (dbs) {
      await Promise.all(dbs.map(db => {
        return new Promise((resolve) => {
          const req = indexedDB.deleteDatabase(db.name)
          req.onsuccess = resolve
          req.onerror = resolve
          req.onblocked = resolve
        })
      }))
    } else {
      // Fallback for browsers without indexedDB.databases()
      await new Promise((resolve) => {
        const req = indexedDB.deleteDatabase('elevatedb')
        req.onsuccess = resolve
        req.onerror = resolve
        req.onblocked = resolve
      })
    }
    localStorage.setItem(DB_VERSION_KEY, String(DB_VERSION))
    console.log('[DB] IndexedDB reset complete')
  } catch (err) {
    console.warn('[DB] Failed to nuke IndexedDB:', err)
  }
}
 
// ─── SYNC FIELD HELPERS ────────────────────────────────────────────────────────
const syncFields = {
  syncStatus: { type: ['string', 'null'] },
  syncError: { type: ['string', 'null'] },
  syncedAt: { type: ['number', 'null'] },
  _modified: { type: 'number' },
  _deleted: { type: 'boolean' },
}
 
// ─── SCHEMAS ──────────────────────────────────────────────────────────────────
 
const productsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    sku_id: { type: ['string', 'null'] },
    name: { type: 'string' },
    category: { type: ['string', 'null'] },
    unit_of_measure: { type: 'string' },
    vat_type: { type: 'string' },
    supplier_id: { type: ['string', 'null'] },
    is_active: { type: 'boolean' },
    ...syncFields,
  },
  required: ['id'],
}
 
const productBranchPricingSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    product_id: { type: 'string' },
    branch_id: { type: 'string' },
    buying_price: { type: 'number' },
    selling_price: { type: 'number' },
    reorder_point: { type: 'number' },
    current_quantity: { type: 'number' },
    last_updated: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id', 'product_id', 'branch_id'],
}

function isDuplicateConflict(error) {
  return Boolean(
    error && (
      error.code === '23505' ||
      error.status === 409 ||
      /duplicate key|conflict/i.test(error.message || '')
    )
  )
}

async function ignoreDuplicateConflict(resultPromise) {
  const result = await resultPromise
  if (result?.error && isDuplicateConflict(result.error)) {
    return { ...result, error: null }
  }
  return result
}
 
const salesSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    branch_id: { type: 'string' },
    customer_id: { type: ['string', 'null'] },
    created_by: { type: ['string', 'null'] },
    date: { type: 'string' },
    subtotal: { type: 'number' },
    discount_amount: { type: 'number' },
    vat_amount: { type: 'number' },
    total_amount: { type: 'number' },
    lifecycle_state: { type: 'string' },
    etims_receipt_no: { type: ['string', 'null'] },
    notes: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
const saleItemsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    sale_id: { type: 'string' },
    product_id: { type: 'string' },
    product_name: { type: 'string' },
    quantity: { type: 'number' },
    unit_price: { type: 'number' },
    vat_applied: { type: 'number' },
    total_amount: { type: 'number' },
    ...syncFields,
  },
  required: ['id', 'sale_id', 'product_id'],
}
 
const salePaymentsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    sale_id: { type: 'string' },
    payment_method: { type: 'string' }, // cash | mpesa | bank_transfer
    amount: { type: 'number' },
    reference: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id', 'sale_id'],
}

const transactionsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    branch_id: { type: 'string' },
    type: { type: 'string' },
    transaction_type_tag: { type: 'string' },
    payment_account: { type: 'string' },
    account_code: { type: ['string', 'null'] },
    date: { type: 'string' },
    created_by: { type: ['string', 'null'] },
    created_at: { type: ['string', 'null'] },
    updated_at: { type: ['string', 'null'] },
    account_id: { type: ['string', 'null'] },
    source_id: { type: ['string', 'null'] },
    source_type: { type: ['string', 'null'] },
    lifecycle_state: { type: 'string' },
    amount: { type: 'number' },
    display_name: { type: 'string' },
    sale_items: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          product_id: { type: 'string' },
          product_name: { type: 'string' },
          quantity: { type: 'number' },
          unit_price: { type: 'number' },
          total_amount: { type: 'number' },
          vat_applied: { type: 'number' },
          etims_receipt_no: { type: ['string', 'null'] },
        },
      },
    },
    expense: {
      type: 'object',
      properties: {
        transaction_id: { type: 'string' },
        category: { type: 'string' },
        amount: { type: 'number' },
        description: { type: ['string', 'null'] },
      },
    },
    transfer: {
      type: 'object',
      properties: {
        id: { type: 'string' },
        business_id: { type: 'string' },
        branch_id: { type: 'string' },
        from_account: { type: 'string' },
        to_account: { type: 'string' },
        amount: { type: 'number' },
        transaction_cost: { type: 'number' },
        date: { type: 'string' },
        note: { type: ['string', 'null'] },
        created_by: { type: ['string', 'null'] },
      },
    },
    costExpense: {
      type: 'object',
      properties: {
        transaction: {
          type: 'object',
          properties: {
            id: { type: 'string' },
            business_id: { type: 'string' },
            branch_id: { type: 'string' },
            type: { type: 'string' },
            transaction_type_tag: { type: 'string' },
            payment_account: { type: 'string' },
            account_code: { type: ['string', 'null'] },
            date: { type: 'string' },
            created_by: { type: ['string', 'null'] },
            source_id: { type: ['string', 'null'] },
            source_type: { type: ['string', 'null'] },
          },
        },
        expense: {
          type: 'object',
          properties: {
            transaction_id: { type: 'string' },
            category: { type: 'string' },
            amount: { type: 'number' },
            description: { type: ['string', 'null'] },
          },
        },
      },
    },
    ...syncFields,
  },
  required: ['id'],
}
 
const expensesSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    branch_id: { type: 'string' },
    stock_entry_id: { type: ['string', 'null'] },
    category: { type: 'string' },
    amount: { type: 'number' },
    description: { type: ['string', 'null'] },
    receipt_url: { type: ['string', 'null'] },
    created_by: { type: ['string', 'null'] },
    date: { type: ['string', 'null'] },
    created_at: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
const stockEntriesSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    branch_id: { type: 'string' },
    product_id: { type: 'string' },
    supplier_id: { type: ['string', 'null'] },
    quantity: { type: 'number' },
    buying_price: { type: 'number' },
    freight_cost: { type: 'number' },
    import_duty: { type: 'number' },
    idf: { type: 'number' },
    rdl: { type: 'number' },
    vat_on_import: { type: 'number' },
    insurance: { type: 'number' },
    additional_costs: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          label: { type: 'string' },
          amount: { type: ['string', 'number'] },
        },
      },
    },
    total_cost: { type: 'number' },
    sourcing_type: { type: 'string' }, // local | import
    created_by: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
const stockTransfersSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    from_branch_id: { type: 'string' },
    to_branch_id: { type: 'string' },
    product_id: { type: 'string' },
    quantity: { type: 'number' },
    unit_cost: { type: 'number' },
    reason: { type: ['string', 'null'] },
    status: { type: 'string' }, // pending | in_transit | received | cancelled
    initiated_by: { type: ['string', 'null'] },
    received_by: { type: ['string', 'null'] },
    received_at: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
const stockTakesSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    branch_id: { type: 'string' },
    type: { type: 'string' }, // full | partial
    status: { type: 'string' }, // draft | in_progress | pending_approval | approved | cancelled
    counted_by: { type: ['string', 'null'] },
    approved_by: { type: ['string', 'null'] },
    start_date: { type: 'string' },
    end_date: { type: ['string', 'null'] },
    notes: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
const stockTakeItemsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    stock_take_id: { type: 'string' },
    product_id: { type: 'string' },
    expected_quantity: { type: 'number' },
    actual_quantity: { type: ['number', 'null'] },
    variance: { type: ['number', 'null'] },
    ...syncFields,
  },
  required: ['id', 'stock_take_id', 'product_id'],
}
 
const customersSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    name: { type: 'string' },
    phone: { type: ['string', 'null'] },
    email: { type: ['string', 'null'] },
    is_active: { type: 'boolean' },
    ...syncFields,
  },
  required: ['id'],
}
 
const creditAccountsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    sale_id: { type: 'string' },
    customer_id: { type: 'string' },
    branch_id: { type: 'string' },
    total_amount: { type: 'number' },
    amount_paid: { type: 'number' },
    balance: { type: 'number' },
    status: { type: 'string' }, // open | partial | paid | written_off
    due_date: { type: ['string', 'null'] },
    notes: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
const creditRepaymentsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    credit_account_id: { type: 'string' },
    amount: { type: 'number' },
    payment_method: { type: 'string' },
    reference: { type: ['string', 'null'] },
    received_by: { type: ['string', 'null'] },
    paid_at: { type: 'string' },
    notes: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
const branchesSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    name: { type: 'string' },
    code: { type: ['string', 'null'] },
    address: { type: ['string', 'null'] },
    phone: { type: ['string', 'null'] },
    email: { type: ['string', 'null'] },
    is_active: { type: 'boolean' },
    status: { type: 'string' },
    ...syncFields,
  },
  required: ['id'],
}
 
const branchAssignmentsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 200 },
    user_id: { type: 'string' },
    branch_id: { type: 'string' },
    role: { type: 'string' },
    is_active: { type: 'boolean' },
    ...syncFields,
  },
  required: ['id', 'user_id', 'branch_id'],
}
 
const branchFloatsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    branch_id: { type: 'string' },
    amount: { type: 'number' },
    updated_by: { type: ['string', 'null'] },
    last_updated: { type: 'string' },
    ...syncFields,
  },
  required: ['id', 'branch_id'],
}
 
const inventoryMovementsSchema = {
  version: 1,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    business_id: { type: 'string' },
    branch_id: { type: 'string' },
    product_id: { type: 'string' },
    movement_type: { type: 'string' },
    quantity_delta: { type: 'number' },
    unit_cost: { type: ['number', 'null'] },
    reference_id: { type: ['string', 'null'] },
    reference_type: { type: ['string', 'null'] },
    created_by: { type: ['string', 'null'] },
    movement_date: { type: ['string', 'null'] },
    ...syncFields,
  },
  required: ['id'],
}
 
// ─── ALL COLLECTIONS ───────────────────────────────────────────────────────────
const ALL_COLLECTIONS = {
  products:                { schema: productsSchema },
  product_branch_pricing:  { schema: productBranchPricingSchema },
  sales:                   { schema: salesSchema },
  sale_items:              { schema: saleItemsSchema },
  sale_payments:           { schema: salePaymentsSchema },
  transactions:            { schema: transactionsSchema },
  expenses:                { schema: expensesSchema },
  stock_entries:           { schema: stockEntriesSchema },
  stock_transfers:         { schema: stockTransfersSchema },
  stock_takes:             { schema: stockTakesSchema },
  stock_take_items:        { schema: stockTakeItemsSchema },
  customers:               { schema: customersSchema },
  credit_accounts:         { schema: creditAccountsSchema },
  credit_repayments:       { schema: creditRepaymentsSchema },
  branches:                { schema: branchesSchema },
  branch_assignments:      { schema: branchAssignmentsSchema },
  branch_floats:           { schema: branchFloatsSchema },
  inventory_movements:     { schema: inventoryMovementsSchema },
}
 
// Noop migration strategies — clean slate, no data to migrate
const noopMigration = (doc) => doc
function migrationStrategies(schema) {
  const strats = {}
  for (let i = 1; i <= schema.version; i++) {
    strats[i] = noopMigration
  }
  return strats
}
 
// ─── DB SINGLETON ──────────────────────────────────────────────────────────────
let dbInstance = null
let dbPromise = null
 
export async function getDb() {
  if (dbInstance) return dbInstance
  if (dbPromise) return dbPromise
 
  dbPromise = (async () => {
    // Hard reset if schema version changed
    if (await shouldResetDb()) {
      console.log('[DB] Schema version mismatch — resetting IndexedDB')
      await nukeIndexedDb()
    }
 
    const db = await createRxDatabase({
      name: 'elevatedb',
      storage: getRxStorageDexie(),
      ignoreDuplicate: true,
    })
 
    const collectionsWithMigration = Object.fromEntries(
      Object.entries(ALL_COLLECTIONS).map(([name, config]) => [
        name,
        {
          ...config,
          migrationStrategies: migrationStrategies(config.schema),
        },
      ])
    )
 
    await db.addCollections(collectionsWithMigration)
    
    // Perform a one-time runtime normalization: if any product documents
    // still contain legacy branch-scoped fields (branch_id, buying_price,
    // selling_price, current_quantity, reorder_point), migrate them into
    // `product_branch_pricing` documents and remove the legacy fields from
    // the product doc. This is non-destructive and runs on startup.
    async function normalizeProductBranchPricing(db) {
      try {
        if (!db.products || !db.product_branch_pricing) return
        const products = await db.products.find().exec()
        for (const p of products) {
          const prod = p.toJSON ? p.toJSON() : p
          const legacyFields = ['branch_id', 'buying_price', 'selling_price', 'current_quantity', 'reorder_point']
          const hasLegacy = legacyFields.some(f => prod[f] !== undefined && prod[f] !== null)
          if (!hasLegacy) continue

          const branchId = prod.branch_id || 'default'
          const pricingId = `${prod.id}:${branchId}`

          const pricingColl = db.product_branch_pricing
          const existing = await pricingColl.findOne(pricingId).exec()
          const now = new Date().toISOString()

          const pricingDoc = {
            id: pricingId,
            product_id: prod.id,
            branch_id: branchId,
            buying_price: prod.buying_price ?? 0,
            selling_price: prod.selling_price ?? 0,
            current_quantity: prod.current_quantity ?? 0,
            reorder_point: prod.reorder_point ?? 0,
            last_updated: now,
            syncStatus: 'pending',
            syncError: null,
            syncedAt: null,
            _modified: Date.now(),
            _deleted: false,
          }

          if (!existing) {
            try {
              await pricingColl.insert(pricingDoc)
            } catch {
              // ignore duplicate insert races
            }
          } else {
            await existing.atomicPatch({
              buying_price: prod.buying_price ?? existing.buying_price,
              selling_price: prod.selling_price ?? existing.selling_price,
              current_quantity: prod.current_quantity ?? existing.current_quantity,
              reorder_point: prod.reorder_point ?? existing.reorder_point,
              last_updated: now,
            })
          }

          // Remove legacy fields from product doc by setting them to null
          const patch = {}
          legacyFields.forEach(f => {
            if (prod[f] !== undefined) patch[f] = null
          })
          if (Object.keys(patch).length) {
            try {
              await p.atomicPatch(patch)
            } catch (err) {
              console.warn('[DB] Failed to remove legacy fields from product', prod.id, err)
            }
          }
        }
        console.log('[DB] Product->product_branch_pricing normalization complete')
      } catch (err) {
        console.warn('[DB] normalization failed', err)
      }
    }

    // Run normalization but don't block DB startup for too long
    normalizeProductBranchPricing(db).catch(err => console.warn('[DB] normalization error', err))

    dbInstance = db
    localStorage.setItem(DB_VERSION_KEY, String(DB_VERSION))
    return db
  })()
 
  return dbPromise
}
 
// ─── REPLICATION REGISTRY ─────────────────────────────────────────────────────
// Prevents duplicate replication instances per collection per business
const replicationRegistry = new Map()
 
function startSingletonReplication(key, createFn) {
  if (replicationRegistry.has(key)) return replicationRegistry.get(key)
  const replication = createFn()
  replicationRegistry.set(key, replication)
  return replication
}
 
export function cancelAllReplications() {
  replicationRegistry.forEach(r => r?.cancel?.())
  replicationRegistry.clear()
}
 
// ─── REPLICATION BUILDER ───────────────────────────────────────────────────────
// Shared pattern for simple pull+push replications
function buildReplication({ key, collection, table, businessId, extraPullFilter, onConflict = 'id' }) {
  return startSingletonReplication(key, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: key,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from(table)
            .select('*')
            .eq('business_id', businessId)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(300)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          if (extraPullFilter) {
            query = extraPullFilter(query)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const result = await ignoreDuplicateConflict(
            supabase.from(table).upsert(stripSyncMetadata(doc), { onConflict })
          )
          if (result?.error) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { error } = await supabase
            .from(table)
            .upsert(stripSyncMetadata(row.newDocumentState), { onConflict })
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── PRODUCTS ─────────────────────────────────────────────────────────────────
export function startProductsReplication(collection, businessId) {
  return buildReplication({
    key: `products-${businessId}`,
    collection,
    table: 'products',
    businessId,
  })
}
 
// ─── PRODUCT BRANCH PRICING ───────────────────────────────────────────────────
// No business_id on this table — filter via product FK join
export function startProductBranchPricingReplication(collection, businessId) {
  return startSingletonReplication(`product_branch_pricing-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `product_branch_pricing-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: async (checkpoint) => {
          // Pull all pricing rows for products belonging to this business
          // Use a subquery instead of inner join to avoid timing issues
          const { data: products } = await supabase
            .from('products')
            .select('id')
            .eq('business_id', businessId)

          const productIds = (products || []).map(product => product.id)

          let query = supabase
            .from('product_branch_pricing')
            .select('*')
            .in('product_id', productIds)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(500)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const { products, _deleted, ...rest } = stripSyncMetadata(doc)
          console.log('[pbp push] attempting upsert', {
            id: rest.id,
            product_id: rest.product_id,
            branch_id: rest.branch_id,
          })
          const result = await supabase
            .from('product_branch_pricing')
            .upsert(rest, { onConflict: 'product_id,branch_id' })
          console.log('[pbp push] result', result)
          if (result?.error && !isDuplicateConflict(result.error)) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { products, _deleted, ...rest } = stripSyncMetadata(row.newDocumentState)
          const { error } = await supabase
            .from('product_branch_pricing')
            .upsert(rest, { onConflict: 'product_id,branch_id' })
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── SALES ────────────────────────────────────────────────────────────────────
export function startSalesReplication(collection, businessId) {
  return buildReplication({
    key: `sales-${businessId}`,
    collection,
    table: 'sales',
    businessId,
  })
}
 
// ─── SALE ITEMS ───────────────────────────────────────────────────────────────
// No business_id — join through sales
export function startSaleItemsReplication(collection, businessId) {
  return startSingletonReplication(`sale_items-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `sale_items-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from('sale_items')
            .select('*, sales!inner(business_id)')
            .eq('sales.business_id', businessId)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(500)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const { sales, ...rest } = stripSyncMetadata(doc)
          const result = await ignoreDuplicateConflict(
            supabase.from('sale_items').upsert(rest)
          )
          if (result?.error) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { sales, ...rest } = stripSyncMetadata(row.newDocumentState)
          const { error } = await supabase.from('sale_items').upsert(rest)
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── SALE PAYMENTS ────────────────────────────────────────────────────────────
export function startSalePaymentsReplication(collection, businessId) {
  return startSingletonReplication(`sale_payments-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `sale_payments-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from('sale_payments')
            .select('*, sales!inner(business_id)')
            .eq('sales.business_id', businessId)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(500)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const { sales, ...rest } = stripSyncMetadata(doc)
          const result = await ignoreDuplicateConflict(
            supabase.from('sale_payments').upsert(rest)
          )
          if (result?.error) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { sales, ...rest } = stripSyncMetadata(row.newDocumentState)
          const { error } = await supabase.from('sale_payments').upsert(rest)
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── EXPENSES ─────────────────────────────────────────────────────────────────
export function startExpensesReplication(collection, businessId) {
  return buildReplication({
    key: `expenses-${businessId}`,
    collection,
    table: 'expenses',
    businessId,
  })
}
 
// ─── STOCK ENTRIES ────────────────────────────────────────────────────────────
export function startStockEntriesReplication(collection, businessId) {
  return buildReplication({
    key: `stock_entries-${businessId}`,
    collection,
    table: 'stock_entries',
    businessId,
  })
}
 
// ─── STOCK TRANSFERS ──────────────────────────────────────────────────────────
export function startStockTransfersReplication(collection, businessId) {
  return buildReplication({
    key: `stock_transfers-${businessId}`,
    collection,
    table: 'stock_transfers',
    businessId,
  })
}
 
// ─── STOCK TAKES ──────────────────────────────────────────────────────────────
export function startStockTakesReplication(collection, businessId) {
  return buildReplication({
    key: `stock_takes-${businessId}`,
    collection,
    table: 'stock_takes',
    businessId,
  })
}
 
// ─── STOCK TAKE ITEMS ─────────────────────────────────────────────────────────
export function startStockTakeItemsReplication(collection, businessId) {
  return startSingletonReplication(`stock_take_items-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `stock_take_items-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from('stock_take_items')
            .select('*, stock_takes!inner(business_id)')
            .eq('stock_takes.business_id', businessId)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(500)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const { stock_takes, ...rest } = stripSyncMetadata(doc)
          const result = await ignoreDuplicateConflict(
            supabase.from('stock_take_items').upsert(rest)
          )
          if (result?.error) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { stock_takes, ...rest } = stripSyncMetadata(row.newDocumentState)
          const { error } = await supabase.from('stock_take_items').upsert(rest)
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── CUSTOMERS ────────────────────────────────────────────────────────────────
export function startCustomersReplication(collection, businessId) {
  return buildReplication({
    key: `customers-${businessId}`,
    collection,
    table: 'customers',
    businessId,
  })
}

// The transactions ledger is replicated as its own table so new local
// source writes can push a denormalized feed row alongside the canonical
// source record.

export function startTransactionsReplication(collection, businessId) {
  return buildReplication({
    key: `transactions-${businessId}`,
    collection,
    table: 'transactions',
    businessId,
  })
}
 
// ─── CREDIT ACCOUNTS ──────────────────────────────────────────────────────────
// No business_id — join through sales
export function startCreditAccountsReplication(collection, businessId) {
  return startSingletonReplication(`credit_accounts-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `credit_accounts-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from('credit_accounts')
            .select('*, sales!inner(business_id)')
            .eq('sales.business_id', businessId)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(300)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const { sales, ...rest } = stripSyncMetadata(doc)
          const result = await ignoreDuplicateConflict(
            supabase.from('credit_accounts').upsert(rest)
          )
          if (result?.error) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { sales, ...rest } = stripSyncMetadata(row.newDocumentState)
          const { error } = await supabase.from('credit_accounts').upsert(rest)
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── CREDIT REPAYMENTS ────────────────────────────────────────────────────────
export function startCreditRepaymentsReplication(collection, businessId) {
  return startSingletonReplication(`credit_repayments-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `credit_repayments-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from('credit_repayments')
            .select('*, credit_accounts!inner(sales!inner(business_id))')
            .eq('credit_accounts.sales.business_id', businessId)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(300)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const { credit_accounts, ...rest } = stripSyncMetadata(doc)
          const result = await ignoreDuplicateConflict(
            supabase.from('credit_repayments').upsert(rest)
          )
          if (result?.error) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { credit_accounts, ...rest } = stripSyncMetadata(row.newDocumentState)
          const { error } = await supabase.from('credit_repayments').upsert(rest)
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── BRANCHES ─────────────────────────────────────────────────────────────────
export function startBranchesReplication(collection, businessId) {
  return buildReplication({
    key: `branches-${businessId}`,
    collection,
    table: 'branches',
    businessId,
  })
}
 
// ─── BRANCH ASSIGNMENTS ───────────────────────────────────────────────────────
export function startBranchAssignmentsReplication(collection, businessId) {
  return startSingletonReplication(`branch-assignments-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `branch-assignments-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: async (checkpoint) => {
          let query = supabase
            .from('user_branch_assignments')
            .select('*, branches!inner(business_id)')
            .eq('branches.business_id', businessId)
            .order('_modified', { ascending: true })
            .order('user_id', { ascending: true })
            .order('branch_id', { ascending: true })
            .limit(300)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
 
          const { data } = await query
          return (data || []).map(row => ({
            id: `${row.user_id}:${row.branch_id}`,
            user_id: row.user_id,
            branch_id: row.branch_id,
            role: row.role,
            is_active: row.is_active,
            syncStatus: 'synced',
            syncError: null,
            syncedAt: row._modified,
            _modified: row._modified,
            _deleted: false,
          }))
        },
        mapDocument: (row) => row,
      },
      push: {
        customInsertHandler: async (doc) => {
          const { error } = await supabase
            .from('user_branch_assignments')
            .upsert(stripSyncMetadata({
              user_id: doc.user_id,
              branch_id: doc.branch_id,
              role: doc.role,
              is_active: doc.is_active,
            }))
          if (error) throw error
          return []
        },
        customUpdateHandler: async (row) => {
          const next = row.newDocumentState
          const { error } = await supabase
            .from('user_branch_assignments')
            .upsert(stripSyncMetadata({
              user_id: next.user_id,
              branch_id: next.branch_id,
              role: next.role,
              is_active: next.is_active,
            }))
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── BRANCH FLOATS ────────────────────────────────────────────────────────────
// No business_id on branch_floats — join through branches
export function startBranchFloatsReplication(collection, businessId) {
  return startSingletonReplication(`branch_floats-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `branch_floats-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from('branch_floats')
            .select('*, branches!inner(business_id)')
            .eq('branches.business_id', businessId)
            .order('last_updated', { ascending: true })
            .order('id', { ascending: true })
            .limit(100)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      push: {
        customInsertHandler: async (doc) => {
          const { branches, ...rest } = stripSyncMetadata(doc)
          const result = await ignoreDuplicateConflict(
            supabase.from('branch_floats').upsert(rest)
          )
          if (result?.error) throw result.error
          return []
        },
        customUpdateHandler: async (row) => {
          const { branches, ...rest } = stripSyncMetadata(row.newDocumentState)
          const { error } = await supabase.from('branch_floats').upsert(rest)
          if (error) throw error
          return true
        },
      },
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
 
// ─── INVENTORY MOVEMENTS (pull-only — written by DB triggers) ─────────────────
export function startInventoryMovementsReplication(collection, businessId) {
  return startSingletonReplication(`inventory_movements-${businessId}`, () => {
    const replication = new SupabaseReplication({
      replicationIdentifier: `inventory_movements-${businessId}`,
      collection,
      supabaseClient: supabase,
      pull: {
        queryBuilder: (checkpoint) => {
          let query = supabase
            .from('inventory_movements')
            .select('*')
            .eq('business_id', businessId)
            .order('_modified', { ascending: true })
            .order('id', { ascending: true })
            .limit(500)
 
          if (checkpoint?.modified) {
            query = query.gt('_modified', checkpoint.modified)
          }
          return query
        },
      },
      // No push — inventory movements are written server-side by triggers only
      live: true,
    })
    attachAutoResync(replication)
    return replication
  })
}
